import React, { useState, useEffect, useRef } from 'react'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { makeStyles, Button, Hidden, DialogTitle, Grid, HtmlContent, Typography, useIsMobile } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteDocument } from 'paintscout'
import type { QuoteItemSection, UpdateableArea } from '@paintscout/util/builder'
import { getArea, getAreaDescription } from '@paintscout/util/builder'
import PencilIcon from '@material-ui/icons/Create'
import { CORRECTIVE_CONSTANT } from '@paintscout/util'
import AreaDimensionsText from '../AreaDimensionsText'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 210,
    // minWidth: 0 is usually default, except for flexbox. This is added to enable flexbox containers to be smaller than their contents (e.g., a long areaLabelTitle with 'nowrap')
    '& > div': {
      '& > div': {
        width: '100%'
      }
    },
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  areaLabel: {
    display: 'flex',
    flexDirection: 'row',
    minWidth: 0,
    alignItems: 'center',
    fontSize: '1rem'
  },
  areaLabelTitle: {
    fontWeight: 500,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  areaDescription: {
    marginBottom: theme.spacing() / 4,
    overflow: 'hidden',
    position: 'relative',
    '& div': {
      maxHeight: 63,
      transition: 'all .3s ease-in-out'
    }
  },
  fade: {
    height: 20,
    display: 'block',
    position: 'absolute',
    width: '100%',
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(255,255,255,0) 50%, #fff 100%)',
    opacity: 1,
    [theme.breakpoints.down('sm')]: {
      height: 34
    }
  },
  seeMore: {
    ...theme.typography.overline,
    marginBottom: theme.spacing() / 4,
    cursor: 'pointer'
  },
  areaTotals: {
    display: 'grid',
    paddingLeft: theme.spacing(3),
    gridTemplateColumns: 'auto auto',
    '& p': {
      whiteSpace: 'nowrap',
      alignSelf: 'end',
      marginRight: theme.spacing(3)
    },
    '& h4': {
      textAlign: 'right',
      lineHeight: '1.2em'
    }
  },
  grey: {
    color: theme.paintscout.palette.brand.Neutral.P75,
    fontWeight: 500
  },
  editButton: {
    margin: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      padding: 0
    }
  },
  container: {
    borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    [theme.breakpoints.down('sm')]: {
      borderRight: 'none'
    }
  },
  marginBottom: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 500
  },
  mobileTotals: {
    margin: theme.spacing(2, 2, 0, 2),
    width: 'calc(100% - 32px)',
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    padding: theme.spacing(1, 0),
    '& p': {
      alignSelf: 'center',
      marginRight: theme.spacing(1),
      fontWeight: 500
    },
    '& h4': {
      fontWeight: 400
    }
  }
}))

export interface EditAreaDialogHeaderProps {
  quote: QuoteDocument
  area: UpdateableArea
  showEdit?: boolean
  section?: QuoteItemSection
  onEdit?: (event: any) => void
}

const EditAreaDialogHeader = (props: EditAreaDialogHeaderProps) => {
  const { quote, showEdit, area, section } = props
  const { options } = useClientOptions()

  const [seeMoreState, setSeeMoreState] = useState({
    seeMore: false,
    useSeeMore: false
  })

  const areaDescription = getAreaDescription({
    quote,
    options,
    areaKey: props.area.key,
    plainText: false,
    section
  })

  const descriptionContent = areaDescription?.useCustom ? areaDescription.custom : areaDescription.default

  const fadeRef = useRef<HTMLInputElement>(null)
  const areaDescriptionRef = useRef<HTMLInputElement>(null)

  const classes = useStyles()

  const mobile = useIsMobile()

  useEffect(() => {
    const descriptionElement = areaDescriptionRef.current

    if (descriptionElement?.scrollHeight > 63 && !seeMoreState.useSeeMore) {
      setSeeMoreState((prevState) => ({ ...prevState, useSeeMore: true }))
    }

    if (descriptionElement?.scrollHeight <= 63 && seeMoreState.useSeeMore) {
      setSeeMoreState((prevState) => ({ ...prevState, useSeeMore: false }))
    }
  }, [seeMoreState.useSeeMore])

  const handleSeeMore = () => {
    const fade = fadeRef.current
    const areaDescription = areaDescriptionRef.current

    if (seeMoreState.seeMore) {
      areaDescription.style.maxHeight = `63px`
      fade.style.opacity = '1'
    } else {
      areaDescription.style.maxHeight = `${areaDescription.scrollHeight}px`
      fade.style.opacity = '0'
    }

    setSeeMoreState((prevState) => ({ ...prevState, seeMore: !seeMoreState.seeMore }))
  }

  const { totals } = getArea({
    quote,
    options,
    key: area.key,
    trimBySubstrateSection: 'bid'
  })

  const hoursValue = totals.hours.useCustom ? totals.hours.custom : totals.hours.default
  const prepValue = totals.prep.useCustom ? totals.prep.custom : totals.prep.default
  const totalHours = Number(hoursValue + prepValue)
  const priceValue = totals.price.useCustom ? totals.price.custom : totals.price.default
  const materialsValue = totals.materials.useCustom ? totals.materials.custom : totals.materials.default
  const headerValue =
    quote.version >= 6
      ? Math.floor((priceValue + materialsValue) * 100 + CORRECTIVE_CONSTANT) / 100
      : priceValue + materialsValue

  const EditButton = () => {
    return (
      <Button onClick={props.onEdit} icon={PencilIcon} className={classes.editButton} variant="text">
        Edit
      </Button>
    )
  }

  const AreaTotals = () => {
    return (
      <div className={classes.areaTotals}>
        <Typography className={classes.marginBottom}>Area Price</Typography>
        <Typography
          className={classes.marginBottom}
          variant="h4"
          format={'price'}
          value={headerValue}
          showUnits
          showZeros
        />
        <Typography className={classes.grey}>Area Hours</Typography>
        <Typography variant="h4" format={'hours'} value={totalHours} classes={{ root: classes.grey }} showZeros />
      </div>
    )
  }

  return (
    <>
      <DialogTitle classes={{ root: classes.root }} rightContent={mobile ? <EditButton /> : <AreaTotals />}>
        <Grid container alignItems="center" className={classes.container}>
          <Grid
            item
            style={{ maxWidth: showEdit && !mobile ? 'calc(100% - 140px)' : 'unset' }}
            className={classes.areaDescriptionContainer}
          >
            <div className={classes.areaLabel}>
              <Typography classes={{ root: classes.areaLabelTitle }} component="div">
                {area.label}
              </Typography>
            </div>

            {descriptionContent && (
              <Typography variant="body1" className={classes.areaDescription} component="div">
                <HtmlContent ref={areaDescriptionRef} id="area-description" content={descriptionContent} />
                {seeMoreState.useSeeMore && <div id="fade" ref={fadeRef} className={classes.fade}></div>}
              </Typography>
            )}
            {seeMoreState.useSeeMore && (
              <Typography onClick={handleSeeMore} variant={'h6'} classes={{ root: classes.seeMore }} component="div">
                {seeMoreState.seeMore ? '(See Less)' : '(See More)'}
              </Typography>
            )}
            <AreaDimensionsText area={area} variant="subtitle2" />
          </Grid>
          {showEdit && !mobile && (
            <Grid item>
              <EditButton />
            </Grid>
          )}
        </Grid>
      </DialogTitle>
      <Hidden mdUp>
        <Grid container alignItems="flex-end" justifyContent="space-between" className={classes.mobileTotals}>
          <Grid item style={{ display: 'flex' }}>
            <Typography className={classes.grey}>Area Hours:</Typography>
            <Typography variant="h4" format={'hours'} value={totalHours} classes={{ root: classes.grey }} showZeros />
          </Grid>
          <Grid item style={{ display: 'flex' }}>
            <Typography>Area Price:</Typography>
            <Typography variant="h4" format={'price'} value={priceValue + materialsValue} showUnits showZeros />
          </Grid>
        </Grid>
      </Hidden>
    </>
  )
}

export default EditAreaDialogHeader
